import styled from 'styled-components';

import { colors, radius } from '../../styles/tokens';

const SIDEBAR_WIDTH = '260px';
const SIDEBAR_WIDTH_COLLAPSED = '80px';
const TOPBAR_HEIGHT = '76px';

export const Container = styled.div`
  min-height: 100vh;
  background-color: ${colors.surface};
`;

export const Overlay = styled.div`
  display: none;

  @media (max-width: 1199px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 99;
    background-color: ${colors.surface}cc;
  }
`;

interface IMenuProps {
  activeMenu: boolean;
  collapsed?: boolean;
}

interface ICollapsedProps {
  collapsed?: boolean;
}

export const Sidebar = styled.aside<IMenuProps>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${({ collapsed }) =>
    collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH};

  display: flex;
  flex-direction: column;

  background-color: ${colors.sidebar};
  z-index: 100;

  overflow-y: auto;
  overflow-x: hidden;

  transition: width 0.3s;

  @media (max-width: 1199px) {
    width: ${SIDEBAR_WIDTH};
    transform: translateX(${({ activeMenu }) => (activeMenu ? '0' : '-100%')});
    transition: transform 0.3s;
  }
`;

export const Brand = styled.div<ICollapsedProps>`
  padding: 32px 24px 24px;

  overflow: hidden;

  /* ButtonMenuMobile é fixed (top:16,left:16, 44x44) por cima de tudo
     -- sem esse respiro, "TrenzoX" nasce embaixo do próprio botão que
     abriu o menu. */
  @media (max-width: 1199px) {
    padding-left: 76px;
  }

  h1 {
    font-size: 20px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: -0.02em;
    color: ${colors.primary};
    white-space: nowrap;

    display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};

    @media (max-width: 1199px) {
      display: block;
    }
  }

  p {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${colors.onSurfaceVariant};
    white-space: nowrap;
    margin-top: 4px;

    display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};

    @media (max-width: 1199px) {
      display: block;
    }
  }
`;

export const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  img {
    width: 26px;
    height: 26px;
    flex-shrink: 0;
  }
`;

export const Nav = styled.div`
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  padding-bottom: 24px;

  > div {
    display: flex;
    flex-direction: column;
  }
`;

export const Divisor = styled.div`
  margin: 12px 20px;
  height: 1px;
  background-color: ${colors.outlineVariant};
`;

export const PlatformTag = styled.span<ICollapsedProps>`
  display: ${({ collapsed }) => (collapsed ? 'none' : 'block')};
  margin: 16px 20px 0;

  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${colors.onSurfaceVariant};
  white-space: nowrap;

  @media (max-width: 1199px) {
    display: block;
  }
`;

export const CollapseButtonRow = styled.div<ICollapsedProps>`
  display: flex;
  justify-content: ${({ collapsed }) => (collapsed ? 'center' : 'flex-end')};

  padding: 0 20px 12px;

  @media (max-width: 1199px) {
    display: none;
  }
`;

export const CollapseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;

  width: 32px;
  height: 32px;
  flex-shrink: 0;

  background-color: transparent;
  border: none;
  border-radius: ${radius.DEFAULT};
  cursor: pointer;

  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.surfaceContainerHigh};
  }
`;

export const ButtonMenuMobile = styled.button`
  display: none;

  @media (max-width: 1199px) {
    display: flex;
    align-items: center;
    justify-content: center;

    position: fixed;
    top: 16px;
    left: 16px;
    z-index: 101;

    width: 44px;
    height: 44px;

    background-color: ${colors.surfaceContainerHigh};
    border: none;
    border-radius: ${radius.DEFAULT};
  }
`;

export const TopBar = styled.header<ICollapsedProps>`
  position: sticky;
  top: 0;
  z-index: 20;

  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px 16px;

  min-height: ${TOPBAR_HEIGHT};
  padding: 14px 32px;

  margin-left: ${({ collapsed }) =>
    collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH};

  transition: margin-left 0.3s;

  background-color: ${colors.surface}e6;
  backdrop-filter: blur(12px);
  border-bottom: 1px solid ${colors.outlineVariant}30;

  @media (max-width: 1199px) {
    margin-left: 0;
    padding-left: 72px;
  }
`;

export const GymInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
    border: 2px solid ${colors.primary};
  }

  div {
    min-width: 0;
  }

  h2 {
    font-size: 15px;
    font-weight: 700;
    color: ${colors.onSurface};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  p {
    font-size: 11px;
    color: ${colors.onSurfaceVariant};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }
`;

export const PlatformIconBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  width: 40px;
  height: 40px;
  border-radius: 50%;

  background-color: ${colors.primaryContainer};
`;

export const RightActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 12px 16px;

  @media (max-width: 1199px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const GymSwitcher = styled.select`
  height: 36px;
  max-width: 200px;
  padding: 0 10px;

  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  background-color: ${colors.surfaceContainer};
  border: 1px solid ${colors.outlineVariant};
  border-radius: 8px;
  cursor: pointer;

  font-size: 12px;
  font-weight: 600;
  color: ${colors.onSurface};

  @media (max-width: 480px) {
    max-width: 140px;
  }
`;

export const ModeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;

  padding: 3px;
  background-color: ${colors.surfaceContainer};
  border: 1px solid ${colors.outlineVariant};
  border-radius: 8px;
`;

interface IModeToggleButtonProps {
  active: boolean;
}

export const ModeToggleButton = styled.button<IModeToggleButtonProps>`
  height: 30px;
  padding: 0 12px;

  background-color: ${({ active }) =>
    active ? colors.primary : 'transparent'};
  border: none;
  border-radius: 6px;
  cursor: pointer;

  font-size: 12px;
  font-weight: 700;
  color: ${({ active }) =>
    active ? colors.onPrimary : colors.onSurfaceVariant};

  transition: background-color 0.15s ease;
`;

export const NotificationButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  width: 40px;
  height: 40px;

  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;

  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.surfaceContainerHigh};
  }
`;

export const NotificationDot = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;

  width: 8px;
  height: 8px;
  border-radius: ${radius.full};

  background-color: ${colors.error};
  border: 1.5px solid ${colors.surface};
`;

export const UserInfo = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;

  background: transparent;
  border: none;
  cursor: pointer;

  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
  }

  div {
    display: none;
    text-align: right;

    @media (min-width: 700px) {
      display: block;
    }
  }

  h3 {
    font-size: 13px;
    font-weight: 700;
    color: ${colors.onSurface};
  }

  p {
    font-size: 11px;
    color: ${colors.onSurfaceVariant};
  }
`;

export const Main = styled.main<ICollapsedProps>`
  margin-left: ${({ collapsed }) =>
    collapsed ? SIDEBAR_WIDTH_COLLAPSED : SIDEBAR_WIDTH};
  min-height: calc(100vh - ${TOPBAR_HEIGHT});

  transition: margin-left 0.3s;

  @media (max-width: 1199px) {
    margin-left: 0;
  }
`;
